import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LanguageToggle from '../components/LanguageToggle';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, Bug, ShieldCheck } from 'lucide-react';
import { publicApi } from '../lib/api';
import { useConfig } from '../context/ConfigContext';
import { useLang } from '../lib/i18n';

const SERVICES = ['General Pest Control', 'Termite Inspection / Treatment', 'Rodent Control', 'Bed Bugs', 'Ants / Roaches', 'Bee / Wasp Removal', 'Commercial Service', 'Other'];

export default function BookService() {
  const { contact } = useConfig();
  const { t } = useLang();
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', service: '', preferredDate: '', notes: '', smsConsent: false });
  const [status, setStatus] = useState('idle'); // idle | sending | done
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.name.trim() || !form.phone.trim()) { setErr(t('book.errNamePhone')); return; }
    setStatus('sending');
    try {
      await publicApi.createLead(form);
      setStatus('done');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || t('book.errGeneric'));
      setStatus('idle');
    }
  };

  const phone = contact.phone1 || '562-215-8002';
  const company = contact.companyName || 'Xpertpests Solutions';

  return (
    <div className="bg-white" data-testid="book-service-page">
      <Header />
      <section className="max-w-2xl mx-auto px-6 py-14">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[#8BC53F] font-bold tracking-widest text-sm"><Bug className="w-4 h-4" />{t('book.badge')}</div>
          <LanguageToggle />
        </div>
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mt-3">{t('book.title')}</h1>
        <p className="text-slate-500 mt-3">{t('book.subtitle')} <a href={`tel:${phone}`} className="text-[#8BC53F] font-semibold">{phone}</a>.</p>

        {status === 'done' ? (
          <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="book-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h2 className="text-2xl font-extrabold text-slate-900 mt-4">{t('book.successTitle')}</h2>
            <p className="text-slate-600 mt-2">{t('book.successMsg', { name: form.name.split(' ')[0] })}</p>
            <Link to="/" className="inline-block mt-6 text-[#6FA82C] font-semibold underline">{t('common.backHome')}</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="book-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><Label className="text-sm">{t('book.name')}</Label><Input value={form.name} onChange={set('name')} className="mt-1.5 h-11" data-testid="book-name" placeholder={t('book.namePh')} /></div>
              <div><Label className="text-sm">{t('book.phone')}</Label><Input value={form.phone} onChange={set('phone')} className="mt-1.5 h-11" data-testid="book-phone" placeholder={t('book.phonePh')} /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div><Label className="text-sm">{t('book.email')}</Label><Input type="email" value={form.email} onChange={set('email')} className="mt-1.5 h-11" data-testid="book-email" placeholder={t('book.emailPh')} /></div>
              <div><Label className="text-sm">{t('book.date')}</Label><Input type="date" value={form.preferredDate} onChange={set('preferredDate')} className="mt-1.5 h-11" data-testid="book-date" /></div>
            </div>
            <div><Label className="text-sm">{t('book.address')}</Label><Input value={form.address} onChange={set('address')} className="mt-1.5 h-11" data-testid="book-address" placeholder={t('book.addressPh')} /></div>
            <div>
              <Label className="text-sm">{t('book.need')}</Label>
              <select value={form.service} onChange={set('service')} className="mt-1.5 h-11 w-full border border-slate-200 rounded-md px-3 text-sm" data-testid="book-service">
                <option value="">{t('book.selectService')}</option>
                {SERVICES.map((s) => <option key={s} value={s}>{t('svc.' + s)}</option>)}
              </select>
            </div>
            <div><Label className="text-sm">{t('book.notes')}</Label><textarea value={form.notes} onChange={set('notes')} rows={3} className="mt-1.5 w-full border border-slate-200 rounded-md p-3 text-sm" data-testid="book-notes" placeholder={t('book.notesPh')} /></div>

            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 cursor-pointer" data-testid="book-consent-label">
              <input type="checkbox" checked={form.smsConsent} onChange={(e) => setForm((f) => ({ ...f, smsConsent: e.target.checked }))} className="mt-1 w-4 h-4 accent-[#8BC53F]" data-testid="book-sms-consent" />
              <span className="text-xs text-slate-600 leading-relaxed">
                {t('book.consentMain', { company })}{' '}
                <Link to="/sms-policy" target="_blank" className="text-[#6FA82C] font-semibold underline">{t('book.smsTerms')}</Link> {t('book.consentAnd')}{' '}
                <Link to="/privacy" target="_blank" className="text-[#6FA82C] font-semibold underline">{t('book.privacy')}</Link>.
              </span>
            </label>

            {err && <p className="text-red-600 text-sm" data-testid="book-error">{err}</p>}

            <Button type="submit" disabled={status === 'sending'} className="w-full h-12 bg-[#8BC53F] hover:bg-[#6FA82C] text-base font-bold gap-2" data-testid="book-submit">
              {status === 'sending' ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
              {status === 'sending' ? t('book.sending') : t('book.submit')}
            </Button>
            <p className="text-xs text-slate-400 text-center">{t('book.disclaimer')}</p>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}
